import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { getAuth, getDashboardPath, logoutUser, notifyAuthChanged } from "../auth";

const navLinks = [
    { id: "home", label: "Home", path: "/" },
    { id: "jobs", label: "Find Jobs", path: "/jobs" },
    { id: "employers", label: "Employers", path: "/employee-dashboard", role: "employer" },
    { id: "companies", label: "Companies", path: "/companies" },
    { id: "admin", label: "Admin", path: "/admin-dashboard", role: "admin" },
    { id: "about", label: "About", path: "/about" },
    { id: "pricing", label: "Pricing", path: "/pricing" },
    { id: "contact", label: "Contact", path: "/contact" },
];

const Header = () => {
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [auth, setAuth] = useState(getAuth());
    const { isLoggedIn, user } = auth;
    const dashboardPath = getDashboardPath(user);
    const guestAuthPath = "/login";

    const getInitials = (name) => {
        const words = String(name || "U").trim().split(/\s+/).filter(Boolean);
        return words.slice(0, 2).map((word) => word.charAt(0)).join("").toUpperCase() || "U";
    };

    const handleLogout = async () => {
        await logoutUser({ notify: false });
        setMobileOpen(false);
        navigate("/login", { replace: true });
        notifyAuthChanged();
        setAuth(getAuth());
    };

    const userAvatar = (
        <span className="header-user-avatar">
            {user?.image_url ? <img src={user.image_url} alt={user.name} /> : getInitials(user?.name)}
        </span>
    );

    const authMenu = isLoggedIn ? (
        <div className="header-user-box">
            <Link to={dashboardPath} className="header-user-link">
                {userAvatar}
                <span>{user?.name}</span>
            </Link>
            <button type="button" onClick={handleLogout}>Logout</button>
        </div>
    ) : (
        <Link to={guestAuthPath} className="header-employer-login">
            <span className="header-login-copy">
                Login / Signup
                <span>All Accounts</span>
            </span>
            <span className="icon-user" aria-hidden="true"></span>
        </Link>
    );

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape" && mobileOpen) setMobileOpen(false);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [mobileOpen]);

    useEffect(() => {
        if (mobileOpen) {
            document.body.classList.add("mobile-menu-visible");
        } else {
            document.body.classList.remove("mobile-menu-visible");
        }

        return () => document.body.classList.remove("mobile-menu-visible");
    }, [mobileOpen]);

    useEffect(() => {
        const syncAuth = () => setAuth(getAuth());

        window.addEventListener("auth-change", syncAuth);
        window.addEventListener("storage", syncAuth);

        return () => {
            window.removeEventListener("auth-change", syncAuth);
            window.removeEventListener("storage", syncAuth);
        };
    }, []);

    const visibleNavLinks = navLinks.filter((item) => !item.role || user?.role === item.role);

    return (
        <header className="main-header fixed-header animated slideInDown">
            <div className="mobile-header">
                <div className="logo">
                    <Link to="/">
                        <img src="/logo-main.png" alt="logo" />
                    </Link>
                </div>

                <div className="nav-outer">
                    <div className="outer-box">
                        <div className="login-box">
                            {isLoggedIn ? (
                                <Link to={dashboardPath} className="mobile-header-user">
                                    {userAvatar}
                                </Link>
                            ) : (
                                <Link to={guestAuthPath} className="mobile-employer-login">
                                    <span className="header-login-copy">
                                        Login / Signup
                                        <span>All Accounts</span>
                                    </span>
                                    <span className="icon-user" aria-hidden="true"></span>
                                </Link>
                            )}
                        </div>

                        <button
                            type="button"
                            className={`mobile-nav-toggler ${mobileOpen ? "active" : ""}`}
                            aria-label="Toggle menu"
                            aria-expanded={mobileOpen}
                            onClick={() => setMobileOpen((s) => !s)}
                        >
                            <span></span>
                            <span></span>
                            <span></span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="main-box">
                <div className="nav-outer">
                    <div className="logo-box">
                        <div className="logo">
                            <Link to="/">
                                <img src="/logo-main.png" alt="logo" />
                            </Link>
                        </div>
                    </div>

                    <nav className="nav main-menu">
                        <ul className="navigation">
                            {visibleNavLinks.map((item) => (
                                <li className={item.id === "home" ? "current" : ""} key={item.id}>
                                    <Link to={item.path}>{item.label}</Link>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </div>

                <div className="outer-box">
                    <div className="btn-box">
                        {authMenu}
                    </div>
                </div>

                {typeof document !== "undefined" && createPortal(
                    <>
                        <div
                            className={`mobile-menu-overlay ${mobileOpen ? "open" : ""}`}
                            onClick={() => setMobileOpen(false)}
                        />
                        <div className={`mobile-menu-panel ${mobileOpen ? "open" : ""}`} role="dialog" aria-modal="true">
                            <button className="mobile-menu-close" onClick={() => setMobileOpen(false)} aria-label="Close">&times;</button>
                            <nav className="nav">
                                <ul className="navigation">
                                    {visibleNavLinks.map((item) => (
                                        <li className={item.id === "home" ? "current" : ""} key={item.id}>
                                            <Link to={item.path} onClick={() => setMobileOpen(false)}>{item.label}</Link>
                                        </li>
                                    ))}
                                    {isLoggedIn ? (
                                        <>
                                            <li><Link to={dashboardPath} onClick={() => setMobileOpen(false)}>{user?.name}</Link></li>
                                            <li><button type="button" className="mobile-menu-logout" onClick={handleLogout}>Logout</button></li>
                                        </>
                                    ) : (
                                        <li><Link to="/login" onClick={() => setMobileOpen(false)}>Login / Register</Link></li>
                                    )}
                                </ul>
                            </nav>
                        </div>
                    </>,
                    document.body
                )}
            </div>
        </header>
    );
};

export default Header;
